import { CRASH_ID_KEY } from './utils';
import {
  collectCarriedPeopleFromMatchDetails,
  collectPeopleFromCrash,
  getPeopleIncludedInMerge,
  getPersonKey,
} from './match-display-rows';
import {
  buildFieldOwnerRoleMap,
  isEmptyMergeValue,
} from './merge-form-utils';

const MERGE_PERSON_META_KEYS = new Set([
  'isMatchCandidate',
  'includeInMerge',
  'carryRoleLabel',
]);

/**
 * @param {Record<string, unknown>} person
 * @returns {Record<string, unknown>}
 */
export function stripPersonMergeMetadata(person) {
  if (!person || typeof person !== 'object') return person;
  const next = { ...person };
  MERGE_PERSON_META_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

/**
 * @param {Record<string, unknown>[]} people
 * @returns {Record<string, unknown>[]}
 */
export function stripPeopleMergeMetadata(people = []) {
  return people.map(stripPersonMergeMetadata);
}

/**
 * Merges person fields from matched roles into the anchor/base person.
 * Hospital-only fields always prefer the hospital match when present.
 *
 * @param {Record<string, unknown>} basePerson
 * @param {{ police?: Record<string, unknown>, hospital?: Record<string, unknown>, insurance?: Record<string, unknown> }} rolePersons
 * @param {Record<string, { permissions?: number[], Permissions?: number[] }>} [configurationMap]
 * @returns {Record<string, unknown>}
 */
export function mergePersonFromRoles(basePerson, rolePersons = {}, configurationMap = {}) {
  const ownerMap = buildFieldOwnerRoleMap(configurationMap);
  const roles = {
    police: rolePersons.police ?? {},
    hospital: rolePersons.hospital ?? {},
    insurance: rolePersons.insurance ?? {},
  };
  const merged = { ...basePerson };

  const allFieldKeys = new Set([
    ...Object.keys(merged),
    ...Object.keys(roles.police),
    ...Object.keys(roles.hospital),
    ...Object.keys(roles.insurance),
    ...Object.keys(ownerMap),
  ]);

  allFieldKeys.forEach((fieldKey) => {
    const ownerRole = ownerMap[fieldKey];
    if (ownerRole) {
      const ownerValue = roles[ownerRole]?.[fieldKey];
      if (!isEmptyMergeValue(ownerValue)) {
        merged[fieldKey] = ownerValue;
      }
      return;
    }

    if (!isEmptyMergeValue(merged[fieldKey])) return;

    for (const role of ['police', 'hospital', 'insurance']) {
      const value = roles[role]?.[fieldKey];
      if (!isEmptyMergeValue(value)) {
        merged[fieldKey] = value;
        break;
      }
    }
  });

  return merged;
}

/**
 * @param {Record<string, unknown>[]} anchorPeople
 * @param {Array<{ anchorPerson: Record<string, unknown>, matches: Record<string, Record<string, unknown>> }>} confirmedMergePeople
 * @param {Record<string, { permissions?: number[], Permissions?: number[] }>} [configurationMap]
 */
export function enrichPeopleWithRoleMatches(
  anchorPeople,
  confirmedMergePeople = [],
  configurationMap = {},
) {
  if (!Array.isArray(anchorPeople) || confirmedMergePeople.length === 0) {
    return anchorPeople;
  }

  return anchorPeople.map((person) => {
    const pair = confirmedMergePeople.find(
      (entry) => getPersonKey(entry.anchorPerson) === getPersonKey(person),
    );
    if (!pair) return person;

    return mergePersonFromRoles(person, pair.matches, configurationMap);
  });
}

/**
 * @param {Record<string, unknown> | null | undefined} anchorDetail
 * @param {Record<string, unknown> | null | undefined} policeDetail
 * @param {Record<string, unknown> | null | undefined} hospitalDetail
 * @param {Record<string, unknown> | null | undefined} insuranceDetail
 * @param {Array<{ anchorPerson: Record<string, unknown>, matches: Record<string, Record<string, unknown>> }>} [confirmedMergePeople]
 * @param {Record<string, { permissions?: number[], Permissions?: number[] }>} [configurationMap]
 * @returns {Record<string, unknown>[]}
 */
export function buildMergedPeopleForSubmit(
  anchorDetail,
  policeDetail,
  hospitalDetail,
  insuranceDetail,
  confirmedMergePeople = [],
  configurationMap = {},
) {
  const carriedFromMatches = collectCarriedPeopleFromMatchDetails([
    { detail: policeDetail },
    { detail: hospitalDetail },
    { detail: insuranceDetail },
  ]).map(({ carryRoleLabel, ...person }) => person);

  const anchorIncluded = enrichPeopleWithRoleMatches(
    getPeopleIncludedInMerge(collectPeopleFromCrash(anchorDetail ?? {})),
    confirmedMergePeople,
    configurationMap,
  );
  const seenPersonKeys = new Set(anchorIncluded.map((person) => getPersonKey(person)));

  return [
    ...anchorIncluded,
    ...carriedFromMatches.filter((person) => {
      const key = getPersonKey(person);
      if (seenPersonKeys.has(key)) return false;
      seenPersonKeys.add(key);
      return true;
    }),
  ];
}

/**
 * Police crash id first — backend expects Police as merge master.
 * @param {string[]} crashIds
 * @param {string | undefined} policeCrashId
 * @returns {string[]}
 */
export function orderCrashIdsForMerge(crashIds, policeCrashId) {
  const unique = [...new Set(crashIds.filter(Boolean))];
  if (!policeCrashId) return unique;
  return [policeCrashId, ...unique.filter((id) => id !== policeCrashId)];
}

/**
 * @param {Record<string, unknown>[]} mergedPeople
 * @param {Record<string, unknown>[] | undefined} policeUnits
 * @param {Record<string, unknown>[] | undefined} editedUnits
 * @returns {Record<string, unknown>[] | undefined}
 */
function buildMergedUnits(mergedPeople, policeUnits, editedUnits) {
  const policeUnitsList = policeUnits ?? [];
  const editedUnitsList = editedUnits ?? [];

  if (policeUnitsList.length > 0) {
    return policeUnitsList.map((policeUnit, unitIndex) => {
      const editedUnit = editedUnitsList[unitIndex] ?? {};
      return {
        ...policeUnit,
        ...editedUnit,
        people: stripPeopleMergeMetadata(
          mergedPeople.filter((person) => person?.unitIndex === unitIndex),
        ),
      };
    });
  }

  if (editedUnitsList.length > 0) {
    return editedUnitsList.map((unit, unitIndex) => ({
      ...unit,
      people: stripPeopleMergeMetadata(
        mergedPeople.filter((person) => person?.unitIndex === unitIndex),
      ),
    }));
  }

  return undefined;
}

/**
 * Costruisce il crash master per POST /match: scheletro Police + campi mergiati dall'anchor.
 *
 * @param {{
 *   policeDetail: Record<string, unknown>,
 *   editedDetail: Record<string, unknown>,
 *   mergedPeople: Record<string, unknown>[],
 *   mergedImages: string[],
 *   crashIdKey?: string,
 * }} params
 * @returns {Record<string, unknown>}
 */
export function buildPoliceMergeMasterCrash({
  policeDetail,
  editedDetail,
  mergedPeople,
  mergedImages,
  crashIdKey = CRASH_ID_KEY,
}) {
  const policeCrashId = policeDetail?.crash?.[crashIdKey];
  const sanitizedPeople = stripPeopleMergeMetadata(mergedPeople);
  const units = buildMergedUnits(
    sanitizedPeople,
    policeDetail?.units,
    editedDetail?.units,
  );

  const rootPeople = sanitizedPeople.filter(
    (person) => person?.unitIndex === undefined || person?.unitIndex === null,
  );

  const masterCrash = {
    ...policeDetail,
    ...editedDetail,
    done: editedDetail?.done ?? policeDetail?.done,
    road: editedDetail?.road ?? policeDetail?.road,
    crash: {
      ...policeDetail?.crash,
      ...editedDetail?.crash,
      [crashIdKey]: policeCrashId,
      images: mergedImages,
    },
    people: rootPeople,
  };

  if (units !== undefined) {
    masterCrash.units = units;
  } else {
    delete masterCrash.units;
  }

  masterCrash.crash_id = policeCrashId;

  return masterCrash;
}

/**
 * @param {{
 *   mode: string,
 *   policeDetail: Record<string, unknown> | null | undefined,
 *   anchorDetail: Record<string, unknown> | null | undefined,
 *   hospitalDetail: Record<string, unknown> | null | undefined,
 *   insuranceDetail: Record<string, unknown> | null | undefined,
 *   mergedImages: string[],
 *   confirmedMergePeople?: Array<{ anchorPerson: Record<string, unknown>, matches: Record<string, Record<string, unknown>> }>,
 *   configurationMap?: Record<string, { permissions?: number[], Permissions?: number[] }>,
 *   crashIdKey?: string,
 * }} params
 * @returns {{ crash_id: string[], crash: Record<string, unknown> } | null}
 */
export function buildMatchSubmitBody({
  mode,
  policeDetail,
  anchorDetail,
  hospitalDetail,
  insuranceDetail,
  mergedImages,
  confirmedMergePeople = [],
  configurationMap = {},
  crashIdKey = CRASH_ID_KEY,
}) {
  const editedDetail = anchorDetail ?? {};
  const mergedPeople = buildMergedPeopleForSubmit(
    editedDetail,
    policeDetail,
    hospitalDetail,
    insuranceDetail,
    confirmedMergePeople,
    configurationMap,
  );

  const crashIds = [
    policeDetail?.crash?.[crashIdKey],
    hospitalDetail?.crash?.[crashIdKey],
    insuranceDetail?.crash?.[crashIdKey],
  ].filter(Boolean);

  const policeCrashId = policeDetail?.crash?.[crashIdKey];
  const orderedCrashIds = orderCrashIdsForMerge(crashIds, policeCrashId);

  if (mode === 'merging') {
    if (!policeDetail || !policeCrashId) return null;

    return {
      crash_id: orderedCrashIds,
      crash: buildPoliceMergeMasterCrash({
        policeDetail,
        editedDetail,
        mergedPeople,
        mergedImages,
        crashIdKey,
      }),
    };
  }

  const validateMaster = policeDetail ?? editedDetail;
  const validateCrashId = validateMaster?.crash?.[crashIdKey];

  return {
    crash_id: orderCrashIdsForMerge(
      validateCrashId ? [validateCrashId] : [],
      validateCrashId,
    ),
    crash: buildPoliceMergeMasterCrash({
      policeDetail: validateMaster,
      editedDetail: validateMaster,
      mergedPeople,
      mergedImages,
      crashIdKey,
    }),
  };
}

/**
 * @param {Record<string, unknown>} response
 * @returns {string}
 */
export function extractMatchApiErrorMessage(response) {
  if (!response || typeof response !== 'object') return '';
  const data = response.responseData;
  if (data && typeof data === 'object' && typeof data.error === 'string') {
    return data.error;
  }
  if (typeof response.responseMessage === 'string') {
    return response.responseMessage;
  }
  return '';
}
